// src/components/dashboard/AssignmentDetailModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Chip,
  CircularProgress, Avatar, Tooltip, Dialog,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import { getJSON, postJSON } from '../../utils/api';
import { notificationPath } from '../../utils/navigationUtils';

function formatDate(d) {
  if (!d) return null;
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(d) {
  if (!d) return false;
  return new Date(d) < new Date();
}

export default function AssignmentDetailModal({ inv, open, onClose, onUnassigned }) {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [unassignError, setUnassignError] = useState(null);
  const commentsEndRef = useRef(null);

  useEffect(() => {
    if (open && inv?.id) {
      setComments([]);
      setCommentText('');
      setUnassignError(null);
      loadComments();
    }
  }, [open, inv?.id]); // eslint-disable-line

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const loadComments = async () => {
    setCommentsLoading(true);
    try {
      const data = await getJSON(`/api/invitations/${inv.id}/comments`);
      setComments(data || []);
    } catch { /* silent */ }
    finally { setCommentsLoading(false); }
  };

  const handleSend = async () => {
    const text = commentText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const comment = await postJSON(`/api/invitations/${inv.id}/comments`, { text });
      setComments(prev => [...prev, comment]);
      setCommentText('');
    } catch { /* silent */ }
    finally { setSending(false); }
  };

  const handleGoToEditor = () => {
    if (inv.type === 'manuscript_goal') {
      navigate(notificationPath({ kind: 'manuscript_goal', draftId: inv.draftId, goalId: inv.goalId }));
    } else if (inv.type === 'cover_task' || inv.type === 'cover_version' || inv.type === 'cover_offer') {
      if (inv.versionId) {
        navigate(`/bookcover/${inv.draftId}/designer/${inv.versionId}`);
      } else {
        navigate(`/editor/${inv.draftId}/cover`);
      }
    } else {
      navigate(`/editor/${inv.draftId}`);
    }
    onClose();
  };

  const handleUnassignConfirm = async () => {
    setUnassigning(true);
    setUnassignError(null);
    try {
      await postJSON(`/api/invitations/${inv.id}/unassign-self`, {});
      setConfirmOpen(false);
      onClose();
      onUnassigned?.();
    } catch (e) {
      setUnassignError(e.message || 'Failed to unassign. Please try again.');
    } finally {
      setUnassigning(false);
    }
  };

  if (!inv) return null;

  const title = inv.taskName || inv.draftTitle || 'Untitled';
  const overdue = isOverdue(inv.dueDate);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            height: { xs: '95vh', sm: '78vh' },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: '#413B8D', px: 3, py: 2.5, color: '#fff', flexShrink: 0, position: 'relative' }}>
          <Typography variant="h5" fontWeight={700} sx={{ wordBreak: 'break-word', lineHeight: 1.3, pr: 4 }}>
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute', top: 10, right: 10,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* Left — assignment details */}
          <Box sx={{ flex: '1.2 1 0', p: 3, overflowY: 'auto', borderRight: '1px solid #eef2f5' }}>

            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                <Typography variant="body2" fontWeight={700}>Book:</Typography>
                <Typography variant="body2">{inv.draftTitle || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={700}>Due Date:</Typography>
                <Typography variant="body2" color={overdue ? '#ef4444' : 'text.primary'} fontWeight={overdue ? 700 : 400}>
                  {formatDate(inv.dueDate) || '—'}
                </Typography>
                {overdue && (
                  <Chip size="small" label="Overdue" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 700 }} />
                )}
              </Box>
            </Box>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="body2" fontWeight={700}>Author:</Typography>
              <Tooltip title={inv.fromUsername || '?'}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: '#7c3aed', fontSize: '0.72rem', cursor: 'default' }}>
                  {(inv.fromUsername || '?')[0].toUpperCase()}
                </Avatar>
              </Tooltip>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={700} gutterBottom>Detail:</Typography>
              {(inv.taskDetail || inv.message) ? (
                <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 1, px: 2, py: 1.5, borderLeft: '3px solid #413B8D' }}>
                  <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {inv.taskDetail || inv.message}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.disabled">—</Typography>
              )}
            </Box>
          </Box>

          {/* Right — comment history */}
          <Box sx={{
            flex: '1 1 0', p: 3, display: 'flex', flexDirection: 'column',
            bgcolor: '#faf8ff', minHeight: 0,
          }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
              Comment History
            </Typography>

            <Box sx={{ flex: 1, overflowY: 'auto', mb: 1.5 }}>
              {commentsLoading ? (
                <Box display="flex" justifyContent="center" pt={3}>
                  <CircularProgress size={22} />
                </Box>
              ) : comments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ pt: 3, fontStyle: 'italic' }}>
                  No comments yet.
                </Typography>
              ) : (
                comments.map(c => (
                  <Box key={c.id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography variant="caption" fontWeight={700} sx={{ color: c.isMine ? '#413B8D' : '#111827' }}>
                        {c.isMine ? 'Me' : c.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(c.createdAt).toLocaleString('en-US', {
                          month: 'short', day: 'numeric',
                          hour: 'numeric', minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                    <Box sx={{
                      bgcolor: c.isMine ? 'rgba(65,59,141,0.05)' : '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 1.5, px: 1.5, py: 1,
                    }}>
                      <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.5 }}>
                        {c.text}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
              <div ref={commentsEndRef} />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <TextField
                multiline
                maxRows={3}
                placeholder="Start type here..."
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
                }}
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, bgcolor: '#fff', fontSize: '0.85rem',
                    '& fieldset': { borderColor: '#d1d5db' },
                    '&:hover fieldset': { borderColor: '#413B8D' },
                    '&.Mui-focused fieldset': { borderColor: '#413B8D' },
                  },
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={!commentText.trim() || sending}
                sx={{
                  bgcolor: '#413B8D', color: '#fff', borderRadius: 2,
                  width: 36, height: 36, alignSelf: 'flex-end', flexShrink: 0,
                  '&:hover': { bgcolor: '#5A52A3' },
                  '&.Mui-disabled': { bgcolor: '#d1d5db', color: '#9ca3af' },
                }}
              >
                {sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{
          display: 'flex', gap: 1.5, px: 3, py: 2,
          borderTop: '1px solid #eef2f5', justifyContent: 'flex-end',
          flexShrink: 0, flexWrap: 'wrap',
        }}>
          {unassignError && (
            <Typography variant="caption" color="error" sx={{ alignSelf: 'center', mr: 'auto' }}>
              {unassignError}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleGoToEditor}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#413B8D', '&:hover': { bgcolor: '#5A52A3' } }}
          >
            Go To Book Editor
          </Button>
          <Button
            variant="contained"
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
          >
            Unassign Self
          </Button>
          <Button
            onClick={onClose}
            variant="text"
            sx={{ textTransform: 'none', color: '#6b7280', '&:hover': { color: '#413B8D' } }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Unassign confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !unassigning && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ bgcolor: '#413B8D', px: 3, py: 2, color: '#fff' }}>
          <Typography fontWeight={700}>Unassign Self</Typography>
        </Box>
        <Box sx={{ px: 3, py: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            Unassigning yourself from an assignment will remove you from all tasks associated to this assignment and you will no longer have access to this book. Do you want to continue?
          </Typography>
          {unassignError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              {unassignError}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, px: 3, pb: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={unassigning}
            variant="text"
            sx={{ textTransform: 'none', color: '#6b7280' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUnassignConfirm}
            disabled={unassigning}
            variant="contained"
            startIcon={unassigning ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
          >
            Yes, Unassign Me
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
